/**
 * AppointmentForm — the form fields + submit logic that used to live inside
 * ScheduleAppointmentDialog. Extracted so it can render in either a Dialog
 * (the existing edit-from-event-tap flow) or inside the new AppointmentDrawer
 * (the create flow with chooser step).
 *
 * Self-contained: owns its own LocalizationProvider, state, and action row.
 * Parent only supplies the cancel handler + an optional success callback.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Typography,
  Paper,
  Autocomplete,
  SelectChangeEvent,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays } from 'date-fns';
import { usePets } from '../../contexts/PetContext';
import { useAuth } from '../../contexts/AuthContext';
import { calendarService } from '../../services/calendarService';
import { careTeamService, CareProvider, ProviderType } from '../../services/careTeamService';
import type { CalendarEvent } from '../../types/calendar';
import { logger } from '../../services/logger';

const log = logger.createScope('AppointmentForm');

interface AppointmentFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
  eventToEdit?: Partial<CalendarEvent> | null;
  initialDate?: Date | null;
  /** When true, render compact buttons + omit the verification banner padding.
   *  Used by AppointmentDrawer where the surrounding shell already provides chrome. */
  compact?: boolean;
}

interface AppointmentType {
  id: string;
  canonical: NonNullable<CalendarEvent['appointmentType']>;
  name: string;
  description: string;
  suggestedDuration: number;
  category: 'medical' | 'grooming' | 'wellness' | 'emergency' | 'other';
  providerType: ProviderType | 'all';
}

const appointmentTypes: AppointmentType[] = [
  { id: 'vet-checkup',  canonical: 'checkup',      name: 'Vet Checkup',     description: 'Regular wellness examination', suggestedDuration: 30, category: 'medical',   providerType: 'vet' },
  { id: 'vaccination',  canonical: 'vaccination',  name: 'Vaccination',     description: 'Immunization appointments',    suggestedDuration: 15, category: 'medical',   providerType: 'vet' },
  { id: 'dental',       canonical: 'dental',       name: 'Dental Cleaning', description: 'Professional dental care',     suggestedDuration: 60, category: 'medical',   providerType: 'vet' },
  { id: 'surgery',      canonical: 'surgery',      name: 'Surgery',         description: 'Surgical procedure',           suggestedDuration: 90, category: 'medical',   providerType: 'vet' },
  { id: 'consultation', canonical: 'consultation', name: 'Consultation',    description: 'Specialist consultation',      suggestedDuration: 45, category: 'medical',   providerType: 'vet' },
  { id: 'grooming',     canonical: 'grooming',     name: 'Grooming',        description: 'Bath, nail trim, haircut',     suggestedDuration: 90, category: 'grooming',  providerType: 'wellness' },
  { id: 'emergency',    canonical: 'emergency',    name: 'Emergency',       description: 'Urgent medical attention',     suggestedDuration: 60, category: 'emergency', providerType: 'vet' },
  { id: 'other',        canonical: 'other',        name: 'Other',           description: 'Custom appointment type',      suggestedDuration: 30, category: 'other',     providerType: 'all' },
];

const STATUS_OPTIONS: Array<{ value: CalendarEvent['status']; label: string }> = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const canonicalToDialogId = (canonical?: CalendarEvent['appointmentType']): string => {
  if (!canonical) return '';
  return appointmentTypes.find((t) => t.canonical === canonical)?.id || '';
};

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  onCancel,
  onSuccess,
  eventToEdit,
  initialDate,
  compact = false,
}) => {
  const { selectedPet, pets } = usePets();
  const { currentUser } = useAuth();

  const isEditMode = !!eventToEdit?.id;
  const isVerifying = !!(eventToEdit?.autoCreated && eventToEdit?.needsVerification);

  const initialState = useMemo(() => {
    if (eventToEdit) {
      const start = eventToEdit.startTime ? new Date(eventToEdit.startTime) : new Date();
      return {
        petId: eventToEdit.petId || selectedPet?.id || '',
        appointmentType: canonicalToDialogId(eventToEdit.appointmentType) || 'other',
        customType: eventToEdit.title || '',
        customDescription: eventToEdit.description || '',
        date: start,
        time: start,
        vetName: eventToEdit.vetName || '',
        location: eventToEdit.location || '',
        notes: eventToEdit.notes || '',
        status: (eventToEdit.status || 'scheduled') as CalendarEvent['status'],
      };
    }
    const baseDate = initialDate || addDays(new Date(), 1);
    return {
      petId: selectedPet?.id || '',
      appointmentType: '',
      customType: '',
      customDescription: '',
      date: baseDate,
      time: new Date(Date.now() + 60 * 60 * 1000),
      vetName: '',
      location: '',
      notes: '',
      status: 'scheduled' as CalendarEvent['status'],
    };
  }, [eventToEdit, initialDate, selectedPet?.id]);

  const [appointmentData, setAppointmentData] = useState(initialState);
  const [availableProviders, setAvailableProviders] = useState<CareProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // Re-seed when the wrapping dialog/drawer reopens or target event changes.
  useEffect(() => {
    setAppointmentData(initialState);
  }, [initialState]);

  const selectedAppointmentType = appointmentTypes.find((t) => t.id === appointmentData.appointmentType);
  const isOtherType = appointmentData.appointmentType === 'other';

  useEffect(() => {
    if (selectedAppointmentType && currentUser) {
      void loadProvidersForType(selectedAppointmentType.providerType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppointmentType?.id, currentUser?.uid]);

  const loadProvidersForType = async (providerType: ProviderType | 'all') => {
    if (!currentUser) return;
    try {
      setLoadingProviders(true);
      const allProviders = await careTeamService.getAllProviders(currentUser.uid);
      const filtered = providerType === 'all'
        ? allProviders
        : allProviders.filter((p) => p.type === providerType);
      const providers = filtered.length === 0 && allProviders.length > 0 ? allProviders : filtered;
      setAvailableProviders(providers);

      if (!isEditMode && providers.length > 0) {
        const first = providers[0];
        setAppointmentData((prev) => ({
          ...prev,
          vetName: prev.vetName || first.name,
          location: prev.location || first.location || '',
        }));
      }
    } catch (error) {
      log.error('Error loading providers', error as Error, { userId: currentUser?.uid, providerType });
      setAvailableProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (field: string, value: any) => {
    setAppointmentData((prev) => ({ ...prev, [field]: value }));
  };

  const resolveProviderByName = (name: string) => {
    if (!name) return null;
    const stripped = name.replace(/^Dr\.\s*/i, '').trim().toLowerCase();
    return availableProviders.find(
      (p) => p.name.toLowerCase() === stripped
        || (p.doctorName || '').toLowerCase() === stripped,
    ) || null;
  };

  const getAppointmentTitle = () => {
    if (isOtherType && appointmentData.customType) return appointmentData.customType;
    return selectedAppointmentType?.name || '';
  };

  const getAppointmentDescription = () => {
    if (isOtherType && appointmentData.customDescription) return appointmentData.customDescription;
    return selectedAppointmentType?.description || '';
  };

  const handleSubmit = async () => {
    if (!appointmentData.petId || !appointmentData.appointmentType) return;
    if (isOtherType && !appointmentData.customType) return;

    setLoading(true);
    try {
      const start = new Date(appointmentData.date);
      start.setHours(appointmentData.time.getHours());
      start.setMinutes(appointmentData.time.getMinutes());
      start.setSeconds(0);
      start.setMilliseconds(0);

      const duration = selectedAppointmentType?.suggestedDuration || 30;
      const end = new Date(start.getTime() + duration * 60_000);

      const provider = resolveProviderByName(appointmentData.vetName);

      const payload: Partial<CalendarEvent> = {
        title: getAppointmentTitle(),
        description: getAppointmentDescription(),
        petId: appointmentData.petId,
        type: 'appointment',
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        allDay: false,
        location: appointmentData.location || provider?.location || '',
        status: appointmentData.status,
        appointmentType: selectedAppointmentType?.canonical,
        duration: duration.toString(),
        notes: appointmentData.notes || '',
        vetName: appointmentData.vetName,
        vetContact: provider?.phone || eventToEdit?.vetContact || '',
        vetEmail: provider?.email || eventToEdit?.vetEmail || '',
      };

      if (isVerifying) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (payload as any).verified = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (payload as any).needsVerification = false;
      }

      if (isEditMode && eventToEdit?.id) {
        await calendarService.updateEvent(eventToEdit.id, payload);
        log.info('Appointment updated', { eventId: eventToEdit.id });
      } else {
        // Manual adds are confirmed by definition — the user wouldn't be
        // entering a slot they hadn't already secured. Stamp `verified: true`
        // so the "Confirmed" pill renders without an extra round-trip.
        const createPayload = {
          ...payload,
          verified: true,
          ownerId: currentUser?.uid || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as Omit<CalendarEvent, 'id'>;
        await calendarService.addEvent(createPayload);
        log.info('Appointment created', { petId: appointmentData.petId });
      }

      onSuccess?.();
      onCancel();
    } catch (error) {
      log.error('Error saving appointment', error as Error, {
        petId: appointmentData.petId,
        userId: currentUser?.uid,
        mode: isEditMode ? 'update' : 'create',
      });
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = (() => {
    if (loading) return isEditMode ? 'Saving…' : 'Scheduling…';
    if (isVerifying) return 'Verify & Save';
    if (isEditMode) return 'Update Appointment';
    return 'Schedule Appointment';
  })();

  const submitDisabled =
    loading
    || !appointmentData.petId
    || !appointmentData.appointmentType
    || (isOtherType && !appointmentData.customType);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Grid container spacing={2}>
          {isVerifying && (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.10) 100%)'
                    : 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(21,101,192,0.05) 100%)',
                  border: (theme) => `1px solid ${theme.palette.info.main}40`,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <InfoIcon sx={{ color: 'info.main', mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} color="info.main" gutterBottom>
                      📧 Auto-Created from Email
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This appointment was automatically extracted from an email. Please review the details and save to confirm.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Pet</InputLabel>
              <Select
                value={appointmentData.petId}
                onChange={(e: SelectChangeEvent) => handleInputChange('petId', e.target.value)}
                label="Pet"
              >
                {pets.map((pet) => (
                  <MenuItem key={pet.id} value={pet.id}>{pet.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={isEditMode ? 8 : 12}>
            <FormControl fullWidth>
              <InputLabel>Appointment Type</InputLabel>
              <Select
                value={appointmentData.appointmentType}
                onChange={(e: SelectChangeEvent) => handleInputChange('appointmentType', e.target.value)}
                label="Appointment Type"
              >
                {appointmentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    <Box>
                      <Typography variant="body1">{type.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description} • {type.suggestedDuration} min
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {isEditMode && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={appointmentData.status}
                  onChange={(e: SelectChangeEvent) =>
                    handleInputChange('status', e.target.value as CalendarEvent['status'])
                  }
                  label="Status"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {isOtherType && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Appointment Type"
                  value={appointmentData.customType}
                  onChange={(e) => handleInputChange('customType', e.target.value)}
                  placeholder="e.g., Behavioral Training, Nutrition Consultation"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={appointmentData.customDescription}
                  onChange={(e) => handleInputChange('customDescription', e.target.value)}
                  placeholder="Brief description of the appointment"
                  multiline
                  rows={2}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Date"
              value={appointmentData.date}
              onChange={(date) => date && handleInputChange('date', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TimePicker
              label="Time"
              value={appointmentData.time}
              onChange={(time) => time && handleInputChange('time', time)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              fullWidth
              disabled={loadingProviders}
              options={availableProviders}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.name
              }
              isOptionEqualToValue={(option, value) =>
                typeof option !== 'string' && typeof value !== 'string' && option.id === value.id
              }
              value={appointmentData.vetName}
              onChange={(_e, newValue) => {
                if (typeof newValue === 'string') {
                  setAppointmentData((prev) => ({ ...prev, vetName: newValue }));
                } else if (newValue) {
                  setAppointmentData((prev) => ({
                    ...prev,
                    vetName: newValue.name,
                    location: prev.location || newValue.location || '',
                  }));
                } else {
                  setAppointmentData((prev) => ({ ...prev, vetName: '' }));
                }
              }}
              onInputChange={(_e, newInput, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setAppointmentData((prev) => ({ ...prev, vetName: newInput }));
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.doctorName && (
                      <Typography variant="caption" color="text.secondary">
                        Dr. {option.doctorName}
                      </Typography>
                    )}
                    {option.location && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {option.location}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Provider/Clinic"
                  placeholder={
                    availableProviders.length === 0 && !loadingProviders
                      ? 'Add providers in Care Team or type a custom name'
                      : 'Pick or type a clinic'
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Location/Address"
              value={appointmentData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="e.g., 123 Main St, City, State"
              helperText={
                resolveProviderByName(appointmentData.vetName)?.location
                  ? 'Auto-populated from selected provider'
                  : ''
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={appointmentData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional information, special instructions, etc."
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: compact ? 2 : 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onCancel} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitDisabled}>
            {submitLabel}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AppointmentForm;
